<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import { useAlert } from 'dashboard/composables';
import WhatsAppStickerAPI from 'dashboard/api/whatsappStickers';

const props = defineProps({
  canManage: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['select-sticker']);

const { t } = useI18n();

const dialogRef = ref(null);
const fileInputRef = ref(null);
const stickers = ref([]);
const isLoading = ref(false);
const isUploading = ref(false);
const deletingStickerId = ref(null);

const hasStickers = computed(() => stickers.value.length > 0);

const loadStickers = async () => {
  isLoading.value = true;

  try {
    const response = await WhatsAppStickerAPI.get();
    stickers.value = response.data?.payload || [];
  } catch (error) {
    useAlert(t('CONVERSATION.REPLYBOX.STICKER_LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const open = async () => {
  dialogRef.value?.open();
  await loadStickers();
};

const close = () => {
  dialogRef.value?.close();
};

const openFileDialog = () => fileInputRef.value?.click();

const uploadSticker = async event => {
  const [file] = event.target.files || [];
  event.target.value = '';

  if (!file) return;

  isUploading.value = true;

  try {
    const response = await WhatsAppStickerAPI.create(file);
    stickers.value = [response.data, ...stickers.value];
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error ||
      t('CONVERSATION.REPLYBOX.STICKER_UPLOAD_ERROR');
    useAlert(errorMessage);
  } finally {
    isUploading.value = false;
  }
};

const removeSticker = async stickerId => {
  deletingStickerId.value = stickerId;

  try {
    await WhatsAppStickerAPI.delete(stickerId);
    stickers.value = stickers.value.filter(sticker => sticker.id !== stickerId);
  } catch (error) {
    useAlert(t('CONVERSATION.REPLYBOX.STICKER_DELETE_ERROR'));
  } finally {
    deletingStickerId.value = null;
  }
};

const selectSticker = sticker => {
  emit('select-sticker', sticker);
  close();
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="lg"
    :title="t('CONVERSATION.REPLYBOX.STICKER_TITLE')"
    :description="t('CONVERSATION.REPLYBOX.STICKER_DESCRIPTION')"
    :show-confirm-button="false"
    :show-cancel-button="false"
  >
    <div class="grid gap-4">
      <div v-if="canManage" class="flex flex-wrap items-center gap-3">
        <input
          ref="fileInputRef"
          accept="image/webp"
          class="hidden"
          type="file"
          @change="uploadSticker"
        />
        <Button
          type="button"
          color="blue"
          icon="i-ph-upload-simple"
          :label="t('CONVERSATION.REPLYBOX.STICKER_UPLOAD')"
          :is-loading="isUploading"
          @click="openFileDialog"
        />
        <span class="text-xs text-n-slate-11">
          {{ t('CONVERSATION.REPLYBOX.STICKER_REQUIREMENTS') }}
        </span>
      </div>

      <div
        v-if="isLoading"
        class="grid place-items-center rounded-xl border border-n-container bg-n-alpha-2 px-4 py-12 text-sm text-n-slate-11"
      >
        {{ t('CONVERSATION.REPLYBOX.STICKER_LOADING') }}
      </div>

      <div
        v-else-if="!hasStickers"
        class="grid place-items-center rounded-xl border border-dashed border-n-container bg-n-alpha-2 px-4 py-12 text-sm text-n-slate-11"
      >
        {{ t('CONVERSATION.REPLYBOX.STICKER_EMPTY') }}
      </div>

      <div v-else class="sticker-grid">
        <div
          v-for="sticker in stickers"
          :key="sticker.id"
          class="sticker-card"
        >
          <button
            type="button"
            class="sticker-card__select"
            @click="selectSticker(sticker)"
          >
            <img
              :src="sticker.file_url"
              :alt="sticker.name"
              class="sticker-card__image"
            />
            <span class="sticker-card__name">
              {{ sticker.name }}
            </span>
          </button>
          <button
            v-if="canManage"
            type="button"
            class="sticker-card__delete"
            :disabled="deletingStickerId === sticker.id"
            @click.stop="removeSticker(sticker.id)"
          >
            <Icon icon="i-lucide-trash-2" class="size-4" />
          </button>
        </div>
      </div>
    </div>
    <template #footer>
      <div class="flex items-center justify-end">
        <Button
          type="button"
          variant="faded"
          color="slate"
          :label="t('DIALOG.BUTTONS.CANCEL')"
          @click="close"
        />
      </div>
    </template>
  </Dialog>
</template>

<style scoped lang="scss">
.sticker-grid {
  @apply grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4;
}

.sticker-card {
  @apply relative rounded-xl border border-n-container bg-n-alpha-2 transition-transform hover:-translate-y-0.5 hover:border-n-brand;
}

.sticker-card__select {
  @apply flex w-full flex-col gap-2 p-3 text-left;
}

.sticker-card__image {
  @apply h-24 w-full object-contain;
}

.sticker-card__name {
  @apply truncate text-xs text-n-slate-11;
}

.sticker-card__delete {
  @apply absolute right-2 top-2 rounded-full bg-n-background p-1 text-n-slate-11 shadow-sm transition-colors hover:bg-n-alpha-3;
}
</style>
